import React, {
  FC,
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import DayPicker, { DateUtils, RangeModifier } from 'react-day-picker';
import {
  format,
  isBefore,
  getYear,
  lastDayOfMonth,
  getDate,
  parseISO,
} from 'date-fns';
import 'react-day-picker/lib/style.css';

import Button from 'components/Button';
import { MONTHS, WEEKDAYS_SHORT } from './localeConfig';
import MonthsNav from './MonthsNav';

import {
  DatePickerButton,
  LayerTouchable,
  ModalContainer,
  ModalFooter,
  ButtonAndModalWrapper,
} from './styles';

interface DatePickerInterface {
  fromDate: Date;
  toDate: Date;
}

interface DatePickerProps {
  setDatePickerValue?: Dispatch<SetStateAction<DatePickerInterface>>;
  handleSaveCalendar: () => void;
  isVisible: boolean;
  initialValue: {
    from?: Date;
    to?: Date;
  };
}

const INITIAL_STATE = {
  from: (null as unknown) as Date,
  to: (null as unknown) as Date,
};

const DatePicker: FC<DatePickerProps> = ({
  setDatePickerValue,
  handleSaveCalendar,
  isVisible,
  initialValue,
}) => {
  const [isModalOpen, toggleModal] = useState(false);
  const [calendarState, setCalendarState] = useState<RangeModifier>(
    INITIAL_STATE,
  );

  const { from, to } = calendarState;
  const modifiers = { start: from, end: to };
  const locale = 'pt';

  useEffect(() => {
    if (setDatePickerValue) setDatePickerValue({ fromDate: from, toDate: to });
  }, [calendarState]);

  useEffect(() => {
    toggleModal(false);
  }, [isVisible]);

  useEffect(() => {
    if (initialValue.from && initialValue.to) {
      const initialFrom = parseISO((initialValue.from as unknown) as string);
      const initialTo = parseISO((initialValue.to as unknown) as string);
      setCalendarState({ from: initialFrom, to: initialTo });
    }
  }, []);

  const handleDayClick = (day) => {
    const today = new Date();
    if (isBefore(day, today)) return;
    const range = DateUtils.addDayToRange(day, calendarState);
    setCalendarState(range);
  };

  const formatedFrom = from && format(from, 'dd/MM/yyyy');
  const formatedTo = to && format(to, 'dd/MM/yyyy');

  const handleCloseModal = (): void => {
    if (!from || !to) {
      setCalendarState(INITIAL_STATE);
    }

    toggleModal(!isModalOpen);
    handleSaveCalendar();
  };

  const selectCurrentSemester = (): void => {
    const currentDate = Date.now();
    const year = getYear(currentDate);
    const middleYearDate = new Date(year, 5, 1);

    if (isBefore(currentDate, middleYearDate)) {
      const dateFrom = new Date();
      const toMonth = new Date(year, 4);
      const lastToMonthDay = lastDayOfMonth(toMonth);
      const dateTo = new Date(year, 4, getDate(lastToMonthDay));
      setCalendarState({ from: dateFrom, to: dateTo });
    } else {
      const dateFrom = new Date();
      const toMonth = new Date(year, 11);
      const lastToMonthDay = lastDayOfMonth(toMonth);
      const dateTo = new Date(year, 11, getDate(lastToMonthDay));
      setCalendarState({ from: dateFrom, to: dateTo });
    }
  };

  const selectNextSemester = (): void => {
    const currentDate = Date.now();
    const year = getYear(currentDate);
    const middleYearDate = new Date(year, 5, 1);

    if (isBefore(currentDate, middleYearDate)) {
      const dateFrom = new Date(year, 6);
      const toMonth = new Date(year, 11);
      const lastToMonthDay = lastDayOfMonth(toMonth);
      const dateTo = new Date(year, 11, getDate(lastToMonthDay));
      setCalendarState({ from: dateFrom, to: dateTo });
    } else {
      const dateFrom = new Date(year + 1, 0);
      const toMonth = new Date(year + 1, 5);
      const lastToMonthDay = lastDayOfMonth(toMonth);
      const dateTo = new Date(year + 1, 5, getDate(lastToMonthDay));
      setCalendarState({ from: dateFrom, to: dateTo });
    }
  };

  return (
    <>
      {isModalOpen && isVisible && (
        // ADD VALIDATION TO CLOSE MODAL
        <LayerTouchable onClick={(): void => toggleModal(!isModalOpen)} />
      )}
      <ButtonAndModalWrapper>
        <DatePickerButton
          role="listbox"
          onClick={(): void => toggleModal(!isModalOpen)}
        >
          {from && to && <span>{`${formatedFrom} até ${formatedTo}`}</span>}

          {(!from && <span>Selecione</span>) || (!to && <span>Selecione</span>)}
        </DatePickerButton>

        {isModalOpen && isVisible && (
          <ModalContainer isModalOpen>
            <DayPicker
              initialMonth={from}
              locale={locale}
              months={MONTHS[locale]}
              weekdaysShort={WEEKDAYS_SHORT[locale]}
              numberOfMonths={2}
              className="Selectable"
              selectedDays={[from, { from, to }]}
              disabledDays={{ before: new Date() }}
              modifiers={modifiers}
              onDayClick={handleDayClick}
              navbarElement={(props) => <MonthsNav {...props} />}
            />

            <ModalFooter>
              <button
                type="button"
                className="select-semestre"
                onClick={selectCurrentSemester}
              >
                Selecionar semestre atual
              </button>

              <button
                type="button"
                className="select-semestre"
                onClick={selectNextSemester}
              >
                Selecionar próximo semestre
              </button>

              <Button title="Salvar" onClick={() => handleCloseModal()} />
            </ModalFooter>
          </ModalContainer>
        )}
      </ButtonAndModalWrapper>
    </>
  );
};

export default DatePicker;
